import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { ErrorStateMatcher, MatSort, MatPaginator } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { combineLatest } from 'rxjs/observable/combineLatest';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import { ApplicationService, DesigneeInfo, RoleInfo, SchoolInfo } from '../../services/application/application.service';
import { AuthService } from '../../services/authentication/auth.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { DownloadService, DownloadFormat, HttpMethod } from '../../services/download/download.service';
import { ErrorService } from '../../services/error/error.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserInfo, UserService } from '../../services/user/user.service';

import { TitleCasePipe } from '../../pipes/titlecase.pipe';

import { validateEmail } from '../../validators/email.validator';

@Component({
    selector: 'app-manage-designees',
    styleUrls: ['managedesignees.component.css'],
    templateUrl: 'managedesignees.component.html',
})
export class ManageDesigneesComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    get NewUserRole() {
        return (this.NewUserForm && this.NewUserForm.controls['Role']) || <FormControl>{};
    }

    DesigneesData: DataTableSource<DesigneeInfo>;
    Designees: DesigneeInfo[];
    AllHeaders: string[] = ['LastName', 'FirstName', 'SchoolName', 'RoleName', 'CanBypassAdmin', 'DesigneeActions'];
    AvailableRoles: RoleInfo[] = [
        { RoleName: 'School Administrator', RoleID: 5, CanBypass: false },
        { RoleName: 'School Counselor', RoleID: 3, CanBypass: true },
    ];
    AvailableSchools: SchoolInfo[];
    FromSearch: boolean;
    NewUserForm: FormGroup;
    NoData: boolean;
    parentErrorStateMatcher: ParentErrorStateMatcher = new ParentErrorStateMatcher();
    PartialViewReady: boolean;
    ShowingNewUserForm: boolean;
    SmallDisplay: boolean;
    User: UserInfo;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private authService: AuthService,
        private dialogService: DialogService,
        private downloadService: DownloadService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private titleCasePipe: TitleCasePipe,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.AvailableSchools = null;
        self.DesigneesData = new DataTableSource<DesigneeInfo>([], self.paginator, self.sorter, self.SortDesignees.bind(self));
        self.Designees = null;
        self.FromSearch = false;
        self.NewUserForm = null;
        self.NoData = false;
        self.PartialViewReady = false;
        self.ShowingNewUserForm = false;
        self.SmallDisplay = false;
        self.User = null;

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
            });

        // Make an empty new user form
        self.NewUserForm = self.fb.group(
            {
                FirstName: [null, Validators.required],
                LastName: [null, Validators.required],
                Email: [null, [Validators.required, validateEmail]],
                EmailVerify: [null, [Validators.required, validateEmail]],
                CanBypassAdmin: [false],
                UserID: [null],
                School: [null, Validators.required],
                Role: [null, Validators.required],
            },
            { validator: [self.EmailVerifyValidator, self.NewDesigneeValidator.bind(self)] }
        );

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            // Load static data for use in the compoment.
            const schoolsObs = self.appService.GetAllSchools(self.User.AuthToken);
            const designeesObs = self.appService.GetAllDesignees(self.User.AuthToken, self.User.CurrentRole);

            combineLatest(schoolsObs, designeesObs, function(schools, designees) {
                return {
                    Schools: schools,
                    Designees: designees,
                };
            })
                .pipe(takeUntil(self._subrelease))
                .subscribe(
                    packet => {
                        self.AvailableSchools = packet.Schools;
                        self.Designees = packet.Designees;

                        self.UpdateDesignees(packet.Designees);

                        // Ready for component use.
                        self.PartialViewReady = true;
                    },
                    error => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    // #region Add New Designee Method

    AddNewDesignee() {
        const self = this;

        const form = <NewDesigneeUserInfo>self.NewUserForm.getRawValue();

        // Get the user confirmation before proceeding.
        const message = `You are about to create the following new designee account in the system:<br /><br />
<strong>First Name: </strong>${self.titleCasePipe.transform(form.FirstName)}<br />
<strong>Last Name: </strong>${self.titleCasePipe.transform(form.LastName)}<br />
<strong>Email Address: </strong>${form.Email}<br />
<strong>School: </strong>${form.School.SchoolName}<br />
<strong>Role: </strong>${form.Role.RoleName}<br />
<strong>Can Bypass Admin: </strong>${form.CanBypassAdmin === true ? 'Yes' : 'No'}<br /><br />
${
    !self.FromSearch
        ? `<strong>This will create a new user account. Once the new user account is created, you will be given ` +
          `the username and randomly generated password for the new account, and the user will be sent an ` +
          `introduction email with directions on how to change their password and access the DELTA System.</strong>`
        : ''
}<br /><br />Are you sure you wish to add the new designee access as summarized above?`;

        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: message,
                OKText: `Yes - Add New Designee Account`,
                CancelText: 'No - CANCEL',
            })
            .subscribe(success => {
                if (success === true) {
                    // Add new designee account
                    self.splashService
                        .DisplaySplash({ TitleText: 'Please Wait', MessageText: 'Creating designee access...' })
                        .subscribe(() => {
                            self.authService
                                .AddNewUserAsDesignee(
                                    self.User.AuthToken,
                                    self.User.CurrentRole,
                                    form.UserID,
                                    form.FirstName,
                                    form.LastName,
                                    form.Email,
                                    form.School.SchoolNumber,
                                    form.Role.RoleID,
                                    form.CanBypassAdmin
                                )
                                .subscribe(
                                    user => {
                                        self.splashService.CloseSplash();

                                        // Was the result returned a new account? If not, then the email address matched an existing
                                        // one. Show a dialog in that case to the system user.
                                        if (!user.NewAccount) {
                                            // Show the dialog
                                            self.dialogService.OpenMessageBoxDialog({
                                                TitleText: 'Duplicate Email Address Detected',
                                                MessageText:
                                                    `The email address provided (${form.Email}) is already attached ` +
                                                    `to an existing account. The existing account information is provided below:` +
                                                    `<br /><br /><strong>Username: </strong>${user.Username}<br />` +
                                                    `<strong>User ID#: </strong>${user.UserID}<br />` +
                                                    `<strong>First Name: </strong>${user.FirstName}<br />` +
                                                    `<strong>Last Name: </strong>${user.LastName}<br />` +
                                                    `<strong>Email: </strong>${user.Email}<br /><br />You cannot create a new account ` +
                                                    `using this email address. If the information above looks appropriate for the ` +
                                                    `desired user, you may provide them with the username shown above. Please note: ` +
                                                    `Designee access has NOT been granted at this time.`,
                                                OKText: 'OK',
                                            });

                                            const input = <HTMLInputElement>document.querySelector('.firstname');
                                            input.focus();

                                            return;
                                        } else {
                                            // Reset and close the form
                                            self.NewUserForm.reset();
                                            self.ShowingNewUserForm = false;

                                            // Update the listed designees with the returned packet (if available)
                                            self.UpdateDesignees(user.Designees);

                                            let msg2 = '';
                                            const school = self.AvailableSchools.filter(item => {
                                                return item.SchoolNumber === user.SchoolNumber;
                                            });

                                            if (self.FromSearch) {
                                                // Display the created designee information
                                                self.splashService.SnackbarSuccess('New Designee Access Created Successfully.');

                                                msg2 =
                                                    `The following designee account was created:<br /><br />
                                        <strong>First Name: </strong>${user.FirstName}<br />
                                        <strong>Last Name: </strong>${user.LastName}<br />
                                        <strong>Email: </strong>${user.Email}<br />
                                        <strong>School: </strong>${
                                            school && school.length ? school[0].SchoolName : '#' + user.SchoolNumber
                                        }<br />
                                        <strong>Role: </strong>${user.RoleName}<br />
                                        <strong>Can Bypass Admin: </strong>${
                                            user.CanBypassAdmin === true ? 'Yes' : 'No'
                                        }<br /><br />This user has <strong>NOT</strong> been sent an email regarding their new ` +
                                                    `designee access rights. You will need to notify them as appropriate regarding ` +
                                                    `their new access.`;
                                            } else {
                                                self.splashService.SnackbarSuccess(
                                                    'New User Account & Designee Access Created Successfully.'
                                                );

                                                msg2 =
                                                    `The following new user account was created:<br /><br />
                                        <strong>Username: </strong>${user.Username}<br />
                                        <strong>Password: </strong>${user.Password}<br />
                                        <strong>Email: </strong>${user.Email}<br />
                                        <strong>Polk User ID#: </strong>${user.UserID}<br />
                                        <strong>School: </strong>${
                                            school && school.length ? school[0].SchoolName : '#' + user.SchoolNumber
                                        }<br />
                                        <strong>Role: </strong>${user.RoleName}<br />
                                        <strong>Can Bypass Admin: </strong>${
                                            user.CanBypassAdmin === true ? 'Yes' : 'No'
                                        }<br /><br />This user <strong>HAS</strong> had an email sent to the provided email address ` +
                                                    `above with instructions on how to reset their default password and access the DELTA ` +
                                                    `System. However, the instructions do not mentioned designee access rights. ` +
                                                    `You will need to notify them as appropriate regarding their new access.`;
                                            }

                                            self.dialogService
                                                .OpenMessageBoxDialog({
                                                    TitleText: 'New Designee Account Information',
                                                    MessageText: msg2,
                                                    OKText: 'OK',
                                                })
                                                .subscribe();
                                        }
                                    },
                                    error => {
                                        self.errorService.HandleServiceError(error);
                                    }
                                );
                        });
                }
            });
    }

    // #endregion

    // #region Download Designees PDF Method

    DownloadDesigneesPDF() {
        const self = this;

        // Set splash
        self.splashService
            .DisplaySplash({ TitleText: 'Please wait', MessageText: 'Downloading Designees PDF...' })
            .pipe(takeUntil(self._subrelease))
            .subscribe(() => {
                self.downloadService
                    .RetrieveDownload(
                        HttpMethod.GET,
                        `Api/Services/PDF/PDFDownloadDesigneesList`,
                        {
                            AuthToken: self.User.AuthToken || '',
                            CurrentRole: self.User.CurrentRole || '',
                            Attachment: true,
                        },
                        DownloadFormat.PDF
                    )
                    .pipe(takeUntil(self._subrelease))
                    .subscribe(
                        success => {
                            // Drop the splash?
                            self.splashService.CloseSplash();
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
            });
    }

    // #endregion

    // #region Email Verify Validate Method

    EmailVerifyValidator(grp: FormGroup) {
        const gval = grp.getRawValue();

        if (gval.Email !== gval.EmailVerify) {
            return {
                EmailVerifyValidate: 'mismatch',
            };
        }

        return null;
    }

    // #endregion

    // #region New Designee Validator Method

    NewDesigneeValidator(grp: FormGroup) {
        const self = this;

        const gval = <NewDesigneeUserInfo>grp.getRawValue();

        let good = true;

        if (!gval.FirstName) {
            good = false;
        }
        if (good && !gval.LastName) {
            good = false;
        }
        if (good && !gval.Email) {
            good = false;
        }
        if (good && !gval.EmailVerify) {
            good = false;
        }
        if (good && gval.UserID == null && self.FromSearch) {
            good = false;
        }
        if (good && !gval.School) {
            good = false;
        }
        if (good && !gval.Role) {
            good = false;
        }

        if (!good) {
            return {
                NewDesigneeValidate: 'required',
            };
        }

        return null;
    }

    // #endregion

    // #region Remove Designee Method

    RemoveDesignee(designee: DesigneeInfo) {
        const self = this;

        if (!designee) {
            return;
        }

        const message =
            `You have chosen to remove the following user as a designee in the system:<br /><br />
        <strong>First Name: </strong>${self.titleCasePipe.transform(designee.FirstName)}<br />
        <strong>Last Name: </strong>${self.titleCasePipe.transform(designee.LastName)}<br />
        <strong>School: </strong>${designee.SchoolName}<br />
        <strong>Role: </strong>${designee.RoleName}<br />
        <strong>Can Bypass Admin: </strong>${designee.CanBypassAdmin === true ? 'Yes' : 'No'}<br /><br />
        Once removed, this user must be added as a designee again in order to have access to student dual ` +
            `enrollment records at this school. Are you sure you wish to continue?`;

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: message,
                OKText: 'Yes - Remove',
                CancelText: 'No - CANCEL',
            })
            .subscribe(result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to remove this person as a designee.
                    self.appService.RemoveDesignee(self.User.AuthToken, self.User.CurrentRole, designee.DesigneeID).subscribe(
                        designees => {
                            // Completed. Refresh the list of designees from what was just returned.
                            self.UpdateDesignees(designees);

                            // Toast the success
                            self.splashService.SnackbarSuccess(`User was removed as a designee successfully.`);
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
                }
            });
    }

    // #endregion

    // #region Reset New User Form Method

    ResetNewUserForm() {
        this.NewUserForm.reset();
        this.ShowingNewUserForm = false;
    }

    // #endregion

    // #region Show New User Form Method

    ShowNewUserForm() {
        this.ShowingNewUserForm = !this.ShowingNewUserForm;
    }

    // #endregion

    // #region Show User Search Method

    ShowUserSearch() {
        const self = this;

        // Pop up a user search dialog.
        self.dialogService
            .OpenUserSearchDialog({
                TitleText: 'DELTA User Search',
                MessageText:
                    `Use the following options to search for an existing user in our district user database. ` +
                    `After a search is conducted, if no results are found or the desired new designee is not found, you can ` +
                    `create a new account for the designee by clicking the "Create New Account" button which will appear ` +
                    `at the bottom of this dialog:`,
                DesigneeMode: true,
            })
            .subscribe(chosenUser => {
                if (chosenUser != null) {
                    // Pre-fill in the new user form with the values returned from the user search just conducted, if a
                    // user was selected. Otherwise, empty the form.
                    if (chosenUser.User) {
                        const patchform = {
                            FirstName: chosenUser.User.FirstName,
                            LastName: chosenUser.User.LastName,
                            Email: chosenUser.User.Email,
                            EmailVerify: chosenUser.User.Email,
                            UserID: chosenUser.User.UserID,
                        };

                        // Patch the new user form.
                        self.NewUserForm.patchValue(patchform);

                        self.NewUserForm.controls['FirstName'].disable();
                        self.NewUserForm.controls['LastName'].disable();
                        self.NewUserForm.controls['Email'].disable();
                        self.NewUserForm.controls['EmailVerify'].disable();
                        self.NewUserForm.controls['UserID'].disable();
                        self.FromSearch = true;
                    } else {
                        self.NewUserForm.reset();

                        self.NewUserForm.controls['FirstName'].enable();
                        self.NewUserForm.controls['LastName'].enable();
                        self.NewUserForm.controls['Email'].enable();
                        self.NewUserForm.controls['EmailVerify'].enable();
                        self.NewUserForm.controls['UserID'].enable();
                        self.FromSearch = false;
                    }

                    // Unlock the new entry panel for editing, but lock out the demographics.
                    self.ShowingNewUserForm = true;
                }
            });
    }

    // #endregion

    // #region Sort Designees Method

    SortDesignees(First: DesigneeInfo, Second: DesigneeInfo) {
        const self = this;

        let PropA: string | number;
        let PropB: string | number;

        switch (self.sorter.active) {
            case 'LastName':
                [PropA, PropB] = [First.LastName, Second.LastName];
                break;
            case 'FirstName':
                [PropA, PropB] = [First.FirstName, Second.FirstName];
                break;
            case 'SchoolName':
                [PropA, PropB] = [First.SchoolName, Second.SchoolName];
                break;
            case 'RoleName':
                [PropA, PropB] = [First.RoleName, Second.RoleName];
                break;
            case 'CanBypassAdmin':
                [PropA, PropB] = [First.CanBypassAdmin === true ? 1 : 0, Second.CanBypassAdmin === true ? 1 : 0];
                break;
        }

        const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
        const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

        return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
    }

    // #endregion

    // #region Update Designees Method

    UpdateDesignees(designees: DesigneeInfo[]) {
        const self = this;

        self.Designees = designees;

        if (!self.Designees || !self.Designees.length) {
            self.NoData = true;

            // Put in a fake record to trip the display message
            self.DesigneesData.Update([{}]);
        } else {
            self.NoData = false;
            self.DesigneesData.Update(self.Designees);
        }

        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
        self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
    }

    // #endregion
}

// #region Custom Classes

// #region New Designee User Info Class

export class NewDesigneeUserInfo {
    FirstName?: string;
    LastName?: string;
    Email?: string;
    EmailVerify?: string;
    UserID?: number;
    School?: SchoolInfo;
    Role?: RoleInfo;
    CanBypassAdmin?: boolean;
}

// #endregion

// #region Parent Error State Matcher Class

export class ParentErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const isSubmitted = !!(form && form.submitted);
        const controlTouched = !!(control && (control.dirty || control.touched));
        const controlInvalid = !!(control && control.invalid);
        const parentInvalid = !!(
            control &&
            control.parent &&
            control.parent.invalid &&
            (control.parent.dirty || control.parent.touched) &&
            control.parent.hasError('EmailVerifyValidate')
        );

        return isSubmitted || (controlTouched && (controlInvalid || parentInvalid));
    }
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/app/managedesignees/managedesignees.component.ts